import { useMemo } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { chaveDoProprio, materiaisPorServico, perfisDosServicos } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { ALTURA_DA_BARRA } from "@/components/layout/mobile/BarraDeAbas";
import { PassoServicos } from "@/components/comecar/PassoServicos";
import { PassoConfirmar } from "@/components/comecar/PassoConfirmar";
import { PassoConfigurar } from "@/components/comecar/configurar/PassoConfigurar";
import type { ServicoEscolhido } from "@/components/comecar/configurar/tipos";
import { useRascunhoDoComecar } from "@/components/comecar/use-rascunho-do-comecar";
import { CancelarCriacao } from "@/components/comecar/CancelarCriacao";

const PASSOS = ["Serviços", "Configurar", "Confirmar"];
const botaoPrimario =
  "inline-flex h-10 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50";

/**
 * Montar o catálogo do laboratório novo: Serviços → Configurar → Confirmar. Tudo parte do serviço:
 * os materiais de cada um vêm da compatibilidade da base e a pessoa ajusta serviço a serviço (com
 * as escalas de cor de cada material). Os perfis (CAD/CAM, cerâmica...) saem dos serviços no fim.
 * Nada vai pro servidor até Concluir, que grava tudo de uma vez.
 */
export function LabComecar() {
  const { user } = useAuth();
  const org = user?.activeOrganization;
  useSetPageHeader({ title: "Montar catálogo", subtitle: org?.name }, [org?.name]);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const utils = trpc.useUtils();
  const base = trpc.labOnboarding.base.useQuery();
  const { rascunho, mudar, descartar } = useRascunhoDoComecar(org?.id);
  const { passo } = rascunho;

  const servicos = useMemo(() => base.data?.servicos ?? [], [base.data]);
  // Começa sem nada marcado (pedido do Jun): nenhum serviço vale pra todo laboratório.
  const servicosMarcados = rascunho.servicos ?? [];

  const concluir = trpc.labOnboarding.concluir.useMutation({
    onSuccess: async ({ criados }) => {
      descartar();
      utils.prosthesis.catalog.invalidate();
      // Espera a sessão saber que o catálogo foi montado — senão o AppLayout ainda vê o laboratório
      // sem catálogo e devolve pro começo do onboarding.
      await utils.me.current.invalidate();
      toast.success(`Catálogo montado com ${criados} itens.`);
      navigate("/inicio", { replace: true });
    },
    onError: (e) => toast.error(e.message || "Não foi possível montar o catálogo."),
  });

  if (base.isLoading || !base.data) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }

  const marcados = new Set(servicosMarcados);
  const escolhidos: ServicoEscolhido[] = [
    ...servicos.filter((s) => marcados.has(s.id)).map((s) => ({ chave: s.id, nome: s.nome, categoria: s.categoria })),
    ...rascunho.servicosProprios.map((p) => ({ chave: chaveDoProprio(p.nome), nome: p.nome, categoria: p.categoria, proprio: true })),
  ];
  const mapa = materiaisPorServico(servicos, servicosMarcados, rascunho.servicosProprios.map((p) => p.nome), rascunho.materiaisPorServico);
  const padraoDe = (chave: string) => servicos.find((s) => s.id === chave)?.materiais ?? [];
  const abrangenciaDe = (chave: string) =>
    rascunho.abrangenciaPorServico[chave] ?? (servicos.find((s) => s.id === chave)?.abrangencia === "ARCADA" ? "ARCADA" : "DENTE");
  // Escala declarada só vale pra material que ficou em algum serviço.
  const usados = new Set(Object.values(mapa).flat());
  const escalasPorMaterial = Object.fromEntries(Object.entries(rascunho.escalasPorMaterial).filter(([m, es]) => usados.has(m) && es.length > 0));
  const irPara = (p: number) => {
    mudar({ passo: p });
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="mx-auto w-full max-w-[1600px] pb-6">
      <CancelarCriacao />
      <ol className="mb-6 flex items-center gap-2">
        {PASSOS.map((p, i) => (
          <li key={p} className="flex flex-1 items-center gap-2">
            <span
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                i < passo ? "bg-brand-600 text-white" : i === passo ? "bg-brand-600 text-white ring-4 ring-brand-100 dark:ring-primary/20" : "bg-zinc-100 text-zinc-500 dark:bg-muted",
              )}
            >
              {i < passo ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={cn("text-sm", i === passo ? "font-semibold text-zinc-900 dark:text-foreground" : "text-zinc-500 dark:text-muted-foreground")}>{p}</span>
            {i < PASSOS.length - 1 && <span className="h-px flex-1 bg-zinc-200 dark:bg-border" />}
          </li>
        ))}
      </ol>

      {passo === 0 && (
        <PassoServicos
          servicos={servicos}
          marcados={servicosMarcados}
          onChange={(ids) => mudar({ servicos: ids })}
          proprios={rascunho.servicosProprios}
          onProprios={(itens) => mudar({ servicosProprios: itens })}
          acao={
            <button type="button" onClick={() => irPara(1)} disabled={escolhidos.length === 0} className={cn(botaoPrimario, "w-full")}>
              Próximo passo
              <ArrowRight className="h-4 w-4" />
            </button>
          }
        />
      )}
      {passo === 1 && (
        <PassoConfigurar
          servicos={escolhidos}
          mapa={mapa}
          padraoDe={padraoDe}
          materiais={base.data.materiais}
          proprios={rascunho.materiaisProprios.map((nome) => ({ nome, familia: rascunho.familiaDosProprios[nome] }))}
          onNovoProprio={(nome, familia) =>
            mudar({
              materiaisProprios: [...rascunho.materiaisProprios, nome],
              familiaDosProprios: familia ? { ...rascunho.familiaDosProprios, [nome]: familia } : rascunho.familiaDosProprios,
            })
          }
          escalas={base.data.escalas}
          escalasPorMaterial={rascunho.escalasPorMaterial}
          abrangenciaDe={abrangenciaDe}
          revisados={rascunho.revisados}
          onRevisado={(chave) => !rascunho.revisados.includes(chave) && mudar({ revisados: [...rascunho.revisados, chave] })}
          onSalvarServico={(chave, ms, es, ab) =>
            mudar({
              materiaisPorServico: { ...rascunho.materiaisPorServico, [chave]: ms },
              escalasPorMaterial: { ...rascunho.escalasPorMaterial, ...es },
              abrangenciaPorServico: { ...rascunho.abrangenciaPorServico, [chave]: ab },
              revisados: [...new Set([...rascunho.revisados, chave])],
            })
          }
          onSalvarGrupo={(chaves, r) =>
            mudar({
              materiaisPorServico: { ...rascunho.materiaisPorServico, ...r.materiaisPorServico },
              escalasPorMaterial: { ...rascunho.escalasPorMaterial, ...r.escalasPorMaterial },
              abrangenciaPorServico: r.abrangencia
                ? { ...rascunho.abrangenciaPorServico, ...Object.fromEntries(chaves.map((c) => [c, r.abrangencia!])) }
                : rascunho.abrangenciaPorServico,
              revisados: [...new Set([...rascunho.revisados, ...chaves])],
            })
          }
        />
      )}
      {passo === 2 && (
        <PassoConfirmar
          servicos={escolhidos}
          mapa={mapa}
          materiais={base.data.materiais}
          escalas={base.data.escalas}
          escalasPorMaterial={escalasPorMaterial}
          abrangenciaDe={abrangenciaDe}
          onEditar={() => irPara(1)}
        />
      )}

      {/* No passo dos serviços a ação mora no resumo; nos outros, no rodapé. */}
      {passo > 0 && (
        <div
          className="sticky z-20 mt-6 rounded-xl border border-zinc-200 bg-white shadow-lg dark:border-border dark:bg-card"
          style={{ bottom: isMobile ? `calc(${ALTURA_DA_BARRA} + 0.75rem + env(safe-area-inset-bottom))` : "1rem" }}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <button
              type="button"
              onClick={() => irPara(passo - 1)}
              className="h-10 rounded-md px-4 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:text-muted-foreground"
            >
              Voltar
            </button>
            {passo === 1 ? (
              <button type="button" onClick={() => irPara(2)} className={botaoPrimario}>
                Próximo passo
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                disabled={concluir.isPending || escolhidos.length === 0}
                onClick={() =>
                  concluir.mutate({
                    perfis: perfisDosServicos(servicos, marcados),
                    servicoIds: servicosMarcados,
                    servicosProprios: rascunho.servicosProprios,
                    materiaisProprios: rascunho.materiaisProprios,
                    materiaisPorServico: mapa,
                    escalasPorMaterial,
                    abrangenciaPorServico: rascunho.abrangenciaPorServico,
                  })
                }
                className={botaoPrimario}
              >
                {concluir.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Concluir
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
